import { Box, IconButton, Stack, Tooltip, Typography } from '@mui/material';
import {
    ReactElement,
    useState,
    useCallback,
    useEffect,
    useLayoutEffect,
    useRef,
    useMemo,
} from 'react';
import TypographyLevel from 'shared/components/Typography';
import { ButtonComp } from 'shared/components/AppComponents';
import {
    uploadZonestyleProgressText,
    uploadZonestyleProgressStyle,
    uploadZonestyleStatusUpload,
    uploadZonestyleChildUploadZone,
    uploadZonestyleFileData,
    uploadZonestyleParentUploadZone,
    uploadZonestyleUploadBox,
    uploadZonestyleRemoveRedIcon,
    uploadZonestyleFileTranslageBg,
    uploadZonestyleBoxButton,
    uploadZoneTypographyErrorHelper,
} from 'assets/styles/modules/upload-documents/upload-packet/UploadZone';
import { DropHandler } from 'shared/helpers/DropHandler';
import { useFormikContext } from 'formik';
import { FormikPropsValues } from './Main';
import InfoIcon from '@mui/icons-material/Info';

/**
 * Represents the UploadFile component.
 *
 * @param {UploadFileProps} props - The component props.
 * @returns {ReactElement} - The rendered component.
 */
const UploadFile = ({
    handleClick,
    state,
    confirmUpload,
    setFileData,
    fileRef,
    callback,
    updating,
    isRework,
    canUpload,
    maxFileRework,
}: UploadFile): ReactElement => {
    const { validateForm } = useFormikContext<FormikPropsValues>();

    const [validatedFileArray, setValidateFileArray] = useState<Array<boolean>>(
        []
    );
    const fileProgressElement = useRef<HTMLDivElement[]>([]);
    const [validatedReasonArray, setValidateReasonArray] = useState<
        Array<string>
    >([]);
    const [isUploadZoneHasFile, setUploadZoneHasFile] =
        useState<boolean>(false);
    const validateReasonByIndex = useCallback<
        (index: number) => Promise<string>
    >(
        async (index: number) => {
            const valid = await validateForm().then((response) => {
                if (
                    response &&
                    Object.keys(response).includes(`attachment`) &&
                    typeof response.attachment === 'object' &&
                    Array.isArray(response.attachment) &&
                    response.attachment.some((o, i) => i === index)
                ) {
                    const reason = response.attachment[index];
                    if (typeof reason === 'object') {
                        return reason.name ?? '';
                    }
                }
                return '';
            });

            return valid;
        },
        [validateForm]
    );

    const isBrowsingFiles = useMemo(() => {
        if (isRework) {
            if (state.validateFile === true && maxFileRework) {
                if (state.fileData.length >= maxFileRework) {
                    return false;
                }
                return true;
            }
            if (state.validateFile && maxFileRework === 0) {
                return true;
            }
        } else if (state.validateFile === true) {
            if (state.fileData.length >= 200) {
                return false;
            }
            return true;
        }
        return false;
    }, [isRework, state.validateFile, state.fileData.length, maxFileRework]);

    const validateFieldNameByIndex = useCallback<
        (index: number) => Promise<boolean>
    >(
        async (index: number) => {
            const valid = await validateForm().then((response) => {
                if (
                    response &&
                    Object.keys(response).includes(`attachment`) &&
                    typeof response.attachment === 'object' &&
                    Array.isArray(response.attachment) &&
                    response.attachment.some((o, i) => i === index)
                ) {
                    return false;
                }
                return true;
            });

            return valid;
        },
        [validateForm]
    );
    const [isDropZone, setIsDropZone] = useState<boolean>(false);
    const [isDropZoneProcessing, setIsDropZoneProcessing] =
        useState<boolean>(false);
    useEffect(() => {
        const validatedArray = Array.from(state.fileData).map(async (_o, i) =>
            validateFieldNameByIndex(i)
        );
        const setledValidateArray = Promise.all(validatedArray);
        const reasonArray = Array.from(state.fileData).map(async (_o, i) =>
            validateReasonByIndex(i)
        );
        const setledreasonArray = Promise.all(reasonArray);
        setledValidateArray.then((response) => {
            setValidateFileArray(response);
        });
        setledreasonArray.then((response) => {
            setValidateReasonArray(response);
            if (state.fileData.length > 0) {
                setUploadZoneHasFile(true);
            } else {
                setUploadZoneHasFile(false);
            }
        });
    }, [state.isChoosingFile, state.fileData.length, isDropZone]);

    useLayoutEffect(() => {
        if (isUploadZoneHasFile) {
            const { current: filesElement } = fileProgressElement;
            // eslint-disable-next-line no-plusplus
            for (let index = 0; index < validatedReasonArray.length; index++) {
                const element = validatedReasonArray[index];
                if (element) {
                    const el = filesElement.at(index);
                    if (el && typeof el.scrollIntoView === 'function') {
                        el?.scrollIntoView({ block: 'center' });
                        break;
                    }
                }
            }
        }
    }, [isUploadZoneHasFile]);
    const getUploadStatus = useCallback(
        (index: number) => {
            if (validatedFileArray.some((o, i) => i === index && o)) {
                return 'Ready to upload';
            }
            return (
                <Typography sx={uploadZoneTypographyErrorHelper}>
                    {validatedReasonArray[index] ?? 'unknown reason'}
                </Typography>
            );
        },
        [validatedFileArray, validatedReasonArray]
    );
    return (
        <Box
            sx={uploadZonestyleParentUploadZone}
            onDragOver={(event: React.DragEvent<HTMLDivElement>) => {
                event.preventDefault();
            }}
            onDrop={async (event: React.DragEvent<HTMLDivElement>) => {
                setIsDropZone(true);
                setIsDropZoneProcessing(true);
                await DropHandler(
                    event,
                    (ev) => {
                        callback(ev);
                        setIsDropZoneProcessing(false);
                    },
                    (ev) => {
                        updating(ev);
                        setIsDropZone(false);
                        setIsDropZoneProcessing(false);
                    },
                    isRework ? 'drop-rework' : 'drop',
                    maxFileRework
                );
            }}
        >
            <Box>
                {state.validateFile ? (
                    <Box>
                        <Box sx={uploadZonestyleChildUploadZone}>
                            {state.fileData &&
                                state.fileData.length > 0 &&
                                state.fileData.map(
                                    (data: any, index: number) => (
                                        <Box sx={uploadZonestyleFileData}>
                                            <Box
                                                sx={{
                                                    ...uploadZonestyleUploadBox,
                                                    marginTop: `${
                                                        index === 0
                                                            ? '2rem'
                                                            : ''
                                                    }`,
                                                    marginBottom: `${
                                                        index ===
                                                        state.fileData.length -
                                                            1
                                                            ? '2rem'
                                                            : ''
                                                    }`,
                                                }}
                                            >
                                                <Box
                                                    sx={
                                                        uploadZonestyleProgressText
                                                    }
                                                >
                                                    <Box
                                                        sx={
                                                            uploadZonestyleFileTranslageBg
                                                        }
                                                    >
                                                        <TypographyLevel
                                                            variant="four"
                                                            sx={
                                                                uploadZonestyleProgressStyle
                                                            }
                                                        >
                                                            {data.name}
                                                        </TypographyLevel>
                                                    </Box>
                                                </Box>
                                                <Box
                                                    sx={
                                                        uploadZonestyleProgressText
                                                    }
                                                >
                                                    <IconButton
                                                        onClick={() => {
                                                            setFileData(index);
                                                            // eslint-disable-next-line no-param-reassign
                                                            fileRef.current.value =
                                                                null;
                                                        }}
                                                        sx={
                                                            uploadZonestyleRemoveRedIcon
                                                        }
                                                        title="remove file"
                                                    >
                                                        <img
                                                            src="/images/icons/RemoveRed.svg"
                                                            alt="Remove file"
                                                            className="UploadZone-IconRemove"
                                                        />
                                                    </IconButton>
                                                    <Box
                                                        ref={(
                                                            ref: HTMLDivElement
                                                        ) => {
                                                            fileProgressElement.current[
                                                                index
                                                            ] = ref as HTMLDivElement;
                                                        }}
                                                        sx={
                                                            uploadZonestyleFileTranslageBg
                                                        }
                                                    >
                                                        <TypographyLevel
                                                            variant="four"
                                                            className="validation-word"
                                                            sx={
                                                                uploadZonestyleStatusUpload
                                                            }
                                                        >
                                                            {getUploadStatus(
                                                                index
                                                            )}
                                                        </TypographyLevel>
                                                    </Box>
                                                </Box>
                                            </Box>
                                        </Box>
                                    )
                                )}
                        </Box>
                        <Box className="UploadZone-boxcenter">
                            <Stack
                                direction="row"
                                sx={uploadZonestyleBoxButton}
                            >
                                <ButtonComp
                                    width="16rem"
                                    onClick={() => handleClick()}
                                    type="neutral"
                                    disabled={isBrowsingFiles === false}
                                >
                                    Browse files
                                </ButtonComp>
                                <ButtonComp
                                    isLoading={state.openDialogFile}
                                    loadingSize={24}
                                    loadingColor="inherit"
                                    loadingText="Loading"
                                    width="16rem"
                                    disabled={validatedFileArray.some(
                                        (o) => o === false
                                    )}
                                    onClick={() => confirmUpload()}
                                    type="secondary"
                                    aria-haspopup="dialog"
                                >
                                    Upload({state.fileData.length})
                                </ButtonComp>
                                {isRework !== true && (
                                    <Tooltip
                                        title={
                                            <>
                                                <Typography color="inherit">
                                                    Expected Initial File
                                                    Format: <br />
                                                    "envelopenumber_"
                                                </Typography>
                                                <br />
                                                <Typography color="inherit">
                                                    "Envelope number :
                                                    Alphanumeric with 12
                                                    characters limit".
                                                </Typography>
                                                <br />
                                                <Typography color="inherit">
                                                    Example:
                                                    <br />
                                                    "11952240Q_filename.zip"
                                                    <br />
                                                    No special characters or
                                                    spaces allowed.
                                                    <br />
                                                    No zero is allowed in front
                                                    of the filename or envelope
                                                    number.
                                                </Typography>
                                            </>
                                        }
                                    >
                                        <IconButton>
                                            <InfoIcon fontSize="small" />
                                        </IconButton>
                                    </Tooltip>
                                )}
                            </Stack>
                        </Box>
                    </Box>
                ) : (
                    <>
                        <Box
                            sx={{
                                background:
                                    'url(/images/brand/UploadIcon.svg) no-repeat center',
                                backgroundSize: 'contain',
                            }}
                            className="UploadZone-uploadIcon"
                            alt="drop a file"
                            draggable={false}
                        />
                        <Box
                            mt={4}
                            draggable={false}
                            sx={{ userSelect: 'none' }}
                        >
                            <TypographyLevel variant="two">
                                <Box sx={{ cursor: 'default' }}>
                                    Drop your file here or
                                </Box>
                                <Box sx={{ cursor: 'default' }}>
                                    use the button below to upload
                                </Box>
                            </TypographyLevel>
                        </Box>
                        <Box
                            mt={4}
                            draggable={false}
                            sx={{ userSelect: 'none' }}
                        >
                            <TypographyLevel variant="four">
                                <Box sx={{ cursor: 'default' }}>{`Only ${
                                    isRework ? '.pdf, .doc, .docx' : '.zip'
                                } files are allowed`}</Box>
                                {isRework ? (
                                    <Box sx={{ cursor: 'default' }}>
                                        {maxFileRework
                                            ? `Maximum ${maxFileRework} files and 2 GB`
                                            : 'Capacity allowed 2 GB'}
                                    </Box>
                                ) : (
                                    <Box sx={{ cursor: 'default' }}>
                                        Maximum 200 files and 2 GB
                                    </Box>
                                )}
                            </TypographyLevel>
                        </Box>
                        <Box
                            my={4}
                            display="flex"
                            justifyContent="center"
                            flexDirection="column"
                            alignItems="center"
                        >
                            <Stack
                                direction="row"
                                className="UploadZone-ButtonCenter"
                            >
                                <ButtonComp
                                    width="16rem"
                                    onClick={() => handleClick()}
                                    type="secondary"
                                    disabled={
                                        typeof canUpload !== 'undefined' &&
                                        !canUpload
                                    }
                                    className="select-upload"
                                    isLoading={
                                        state.isProcessingData ||
                                        isDropZoneProcessing
                                    }
                                    loadingText="PROCESSING"
                                >
                                    Select files to upload
                                </ButtonComp>
                                {isRework !== true && (
                                    <Tooltip
                                        title={
                                            <>
                                                <Typography color="inherit">
                                                    Expected Initial File
                                                    Format: <br />
                                                    "envelopenumber_"
                                                </Typography>
                                                <br />
                                                <Typography color="inherit">
                                                    "Envelope number :
                                                    Alphanumeric with 12
                                                    characters limit".
                                                </Typography>
                                                <br />
                                                <Typography color="inherit">
                                                    Example:
                                                    <br />
                                                    "11952240Q_filename.zip"
                                                    <br />
                                                    No special characters or
                                                    spaces allowed.
                                                    <br />
                                                    No zero is allowed in front
                                                    of the filename or envelope
                                                    number.
                                                </Typography>
                                            </>
                                        }
                                    >
                                        <IconButton>
                                            <InfoIcon fontSize="small" />
                                        </IconButton>
                                    </Tooltip>
                                )}
                            </Stack>
                        </Box>
                    </>
                )}
            </Box>
        </Box>
    );
};

export default UploadFile;
